/**
 * 가구 생성 커널.
 *
 * 규칙:
 *  - 부품은 Mesh 를 직접 만들지 않는다. **재질 슬롯별 writer** 에 지오메트리를 방출하고,
 *    마지막에 슬롯당 하나의 메시로 합쳐진다 (가구 1점 = draw call 3~5개).
 *  - 치수는 매직 숫자가 아니라 **치수 계약(dims)** 에서 파생된다. 상판 두께를 바꾸면
 *    다리 길이와 서랍 높이가 따라 움직여야 한다.
 *  - UV 는 세그먼트 수가 아니라 **실제 크기(m)** 로 만든다. 그래야 같은 나무결이
 *    책상에서도 책장에서도 같은 밀도로 보인다.
 *  - 근거리(1~2m)에서 보므로 모든 모서리에 베벨을 넣는다. 완전한 직각 모서리는
 *    조명이 닿는 순간 CG 티가 난다.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** 재질 슬롯 — 표면 정체성 단위. 가구는 이 조합으로 만들어진다. */
export type Slot =
  | 'oak'
  | 'walnut'
  | 'ebony'
  | 'steel'
  | 'brass'
  | 'fabric'
  | 'leather'
  | 'glass'
  | 'stone'
  | 'paper'
  | 'paint'
  // --- 신당/무속 ---
  /** 옻칠한 나무 — 제기·신단·함 */
  | 'lacquer'
  /** 놋쇠보다 어둡고 푸른 녹이 낀 청동 — 향로·방울 */
  | 'bronze'
  /** 한지 — 등롱·부적·족자. 빛을 머금는다 */
  | 'hanji'
  /** 붉은 제단보·무복 */
  | 'redcloth'
  /** 가죽 — 북·장구 */
  | 'hide'
  /** 그림이 그려진 면 — 무신도·병풍·부적 (텍스처가 정체성) */
  | 'painted'
  /** 발광하는 전구 — 조각별 materials() 가 emissive 재질을 준다 */
  | 'bulb'
  /**
   * **켜져 있는 촛불.** 슬롯을 따로 두는 이유는 재질이 하나로 공유되기 때문이다 —
   * 굽고 나서도 이 재질의 `emissiveIntensity` 하나만 흔들면 맵의 모든 촛불이 함께
   * 일렁인다. 조각마다 재질을 만들면 draw call 도 늘고 흔들 대상도 흩어진다
   */
  | 'flame'
  /** 연탄·숯 — 검회색 다공질. 조각별 materials() 가 색을 준다 */
  | 'coal'
  /** 검은 무쇠 — 반닫이 장석·자물쇠. 스테인리스처럼 밝으면 전통 기물로 안 읽힌다 */
  | 'iron'
  // --- 건축 (docs/ARCHITECTURE_CATALOG.md) ---
  // 회벽은 아래 'plaster' 를, 창틀은 'alu' 를 재사용한다. 슬롯 하나가 draw call 하나라
  // 새로 만들면 손해다 — 색만 다른 슬롯을 늘리지 말 것.
  /** 시멘트 — 목공방·창고 바닥과 봉당 */
  | 'concrete'
  /**
   * 창유리. 'glass' 와 달리 **transmission 을 쓰지 않는다.** transmission 이 있는 메시가
   * 씬에 하나라도 있으면 three 는 프레임마다 불투명 씬을 별도 타깃에 다시 그린다.
   * 창이 서른일곱 장이라 그 비용이 상시로 든다.
   */
  | 'pane'
  | 'frost'
  /** 골슬레이트 지붕 — 파형 강판에 세월이 앉은 것 */
  | 'slate'
  /**
   * 건축 목부재 — 문틀·문선·걸레받이·몰딩. darkwood 표면을 **그대로** 쓴다.
   * walnut 을 쓰면 안 된다: 그쪽은 색을 밝게 하고 roughness 를 0.5 로 낮춘 clone 이라
   * 고주파 노멀과 만나 스페큘러가 흰 점으로 aliasing 한다 (문틀에서 실제로 겪었다).
   */
  | 'trim'
  /** 옹기 — 짙은 갈색 유약을 입힌 질그릇. 영혼 항아리·장독 */
  | 'earthen'
  /** 마른 피 — 검붉고 광택이 죽은 얼룩 */
  | 'blood'
  | 'flesh'
  | 'porcelain'
  /** 회벽 — 벽면 조각(긁힌 자국 판 등) */
  | 'plaster'
  // --- 2026 년. 「사흘」의 마을은 1998 년에 닫은 분교를 청년들이 고쳐 쓰는 곳이다.
  //     골조는 90 년대인데 사는 사람은 지금 사람이라, 그 대비가 소품에서 나야 한다 ---
  /** 사출 플라스틱 — 공유기·리모컨·가전 몸체. 무광이고 미세하게 거칠다 */
  | 'plastic'
  /** 아노다이징한 알루미늄 — 노트북 상판·삼각대. 강철보다 어둡고 반사가 무르다 */
  | 'alu'
  /** 골판지 — 택배 상자. 눈에 갇힌 이 마을이 바깥과 이어진 유일한 물건이다 */
  | 'cardboard'
  /** 고무 — 케이블·그립. 빛을 거의 안 되쏜다 */
  | 'rubber'
  /**
   * 켜져 있는 화면.
   *
   * **시대를 말하는 것은 이 슬롯 하나다.** 어두운 방에 발광하는 사각형이 하나
   * 있으면 그 방은 즉시 지금이 된다. 가구를 아무리 잘 깎아도 이것만 못하다.
   * 그래서 이 슬롯만 가구별 텍스처를 갖는다 — 화면에 뭐가 떠 있는지가 곧 그 사람이다.
   */
  | 'screen';

export interface PartStat {
  name: string;
  slot: Slot;
  triangles: number;
}

/**
 * 방출 단위(add 호출 하나)의 바운딩 박스. `[minX,minY,minZ, maxX,maxY,maxZ]`.
 *
 * **이걸 기록하는 이유:** MeshWriter 는 슬롯별로 지오메트리를 병합하므로 조각을 만든 뒤에는
 * 「어느 부품이 어디 있었는지」가 씬 그래프에 남지 않는다. 그래서 부품이 서로 떨어져
 * 공중에 떠 있어도 아무 검사에 걸리지 않았다 — 투과 게이트는 뚫림만 보고,
 * audit-placement 는 레벨의 FURNITURE 박스만 본다. 조각 **내부**를 보는 눈이 없었다.
 *
 * parts(PartStat)와 따로 두는 이유: parts 는 이름이 같고 연속이면 삼각형을 합산하지만,
 * 연결성은 add 단위로 봐야 한다 (도자기 한 벌의 접시 두 장은 같은 이름이지만 다른 덩이다).
 */
export interface PartBox {
  name: string;
  slot: Slot;
  box: [number, number, number, number, number, number];
  /**
   * 표면 표본점 (xyz 평탄화). **AABB 만으로는 접촉을 판정할 수 없어서** 필요하다 —
   * 45도로 기운 막대의 AABB 는 실제 부피보다 훨씬 커서, 양동이 밖을 지나가는 걸레
   * 자루가 「닿음」으로 나왔다. 여행 가방 뚜껑도 힌지에서 14mm 떨어졌는데 AABB 는
   * 겹쳐 있었다.
   */
  pts: Float32Array;
  /**
   * 삼각형 (정점 9개 float 씩). 표본점끼리의 거리로는 **면 접촉을 잴 수 없다** —
   * 큰 상판 중앙에 놓인 다리는 서로의 표본점이 멀어서 「947mm 떠 있음」으로 나왔다.
   * 점 → 상대 삼각형 거리를 써야 실제 표면 거리가 된다.
   */
  tris: Float32Array;
}

/** PartBox.tris 한 부품당 삼각형 수 상한 — 넘으면 균등 추출한다 */
const TRI_CAP = 2400;

/** 거리 계산에 쓸 표본점 수 상한 (겹침 영역 안에서만 뽑는다) */
const SAMPLE_N = 600;

function surfaceSamples(geo: THREE.BufferGeometry): Float32Array {
  const pos = geo.attributes.position;
  if (!pos) return new Float32Array(0);
  const idx = geo.index;
  const triCount = idx ? Math.floor(idx.count / 3) : Math.floor(pos.count / 3);
  const out: number[] = [];
  // 삼각형 중심 — 면 중앙 접촉을 잡는다
  const tStep = Math.max(1, Math.ceil(triCount / (SAMPLE_N / 2)));
  for (let t = 0; t < triCount; t += tStep) {
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let k = 0; k < 3; k++) {
      const vi = idx ? idx.getX(t * 3 + k) : t * 3 + k;
      cx += pos.getX(vi);
      cy += pos.getY(vi);
      cz += pos.getZ(vi);
    }
    out.push(cx / 3, cy / 3, cz / 3);
  }
  // 정점 — 모서리·꼭짓점 접촉을 잡는다
  const vStep = Math.max(1, Math.ceil(pos.count / (SAMPLE_N / 2)));
  for (let v = 0; v < pos.count; v += vStep) out.push(pos.getX(v), pos.getY(v), pos.getZ(v));
  return new Float32Array(out);
}

function surfaceTris(geo: THREE.BufferGeometry): Float32Array {
  const pos = geo.attributes.position;
  if (!pos) return new Float32Array(0);
  const idx = geo.index;
  const triCount = idx ? Math.floor(idx.count / 3) : Math.floor(pos.count / 3);
  const step = Math.max(1, Math.ceil(triCount / TRI_CAP));
  const out: number[] = [];
  for (let t = 0; t < triCount; t += step) {
    for (let k = 0; k < 3; k++) {
      const vi = idx ? idx.getX(t * 3 + k) : t * 3 + k;
      out.push(pos.getX(vi), pos.getY(vi), pos.getZ(vi));
    }
  }
  return new Float32Array(out);
}

/**
 * 점에서 삼각형까지 거리의 제곱. Ericson, Real-Time Collision Detection 의 최근접점
 * 알고리즘 — 정점·모서리·내부 영역을 나눠 본다.
 */
function ptTri2(
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): number {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  const apx = px - ax, apy = py - ay, apz = pz - az;
  const d1 = abx * apx + aby * apy + abz * apz;
  const d2 = acx * apx + acy * apy + acz * apz;
  if (d1 <= 0 && d2 <= 0) return apx * apx + apy * apy + apz * apz;
  const bpx = px - bx, bpy = py - by, bpz = pz - bz;
  const d3 = abx * bpx + aby * bpy + abz * bpz;
  const d4 = acx * bpx + acy * bpy + acz * bpz;
  if (d3 >= 0 && d4 <= d3) return bpx * bpx + bpy * bpy + bpz * bpz;
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3);
    const qx = apx - v * abx, qy = apy - v * aby, qz = apz - v * abz;
    return qx * qx + qy * qy + qz * qz;
  }
  const cpx = px - cx, cpy = py - cy, cpz = pz - cz;
  const d5 = abx * cpx + aby * cpy + abz * cpz;
  const d6 = acx * cpx + acy * cpy + acz * cpz;
  if (d6 >= 0 && d5 <= d6) return cpx * cpx + cpy * cpy + cpz * cpz;
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const wgt = d2 / (d2 - d6);
    const qx = apx - wgt * acx, qy = apy - wgt * acy, qz = apz - wgt * acz;
    return qx * qx + qy * qy + qz * qz;
  }
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    const wgt = (d4 - d3) / (d4 - d3 + (d5 - d6));
    const qx = bpx + wgt * (cpx - bpx), qy = bpy + wgt * (cpy - bpy), qz = bpz + wgt * (cpz - bpz);
    return qx * qx + qy * qy + qz * qz;
  }
  const denom = 1 / (va + vb + vc);
  const v = vb * denom, wgt = vc * denom;
  const qx = apx - (v * abx + wgt * acx);
  const qy = apy - (v * aby + wgt * acy);
  const qz = apz - (v * abz + wgt * acz);
  return qx * qx + qy * qy + qz * qz;
}

/** 표본점 집합에서 삼각형 집합까지 최소거리 제곱. limit 이내면 즉시 끝낸다 */
function ptsToTris2(pts: Float32Array, tris: Float32Array, lim2: number): number {
  let best = Infinity;
  for (let i = 0; i < pts.length; i += 3) {
    for (let t = 0; t < tris.length; t += 9) {
      const d2 = ptTri2(
        pts[i], pts[i + 1], pts[i + 2],
        tris[t], tris[t + 1], tris[t + 2],
        tris[t + 3], tris[t + 4], tris[t + 5],
        tris[t + 6], tris[t + 7], tris[t + 8],
      );
      if (d2 < best) {
        best = d2;
        if (best <= lim2) return best;
      }
    }
  }
  return best;
}

/** [lo, hi] 박스와 겹치는 삼각형만 골라 낸다 */
function trisInBox(tris: Float32Array, lo: number[], hi: number[]): Float32Array {
  const out: number[] = [];
  for (let t = 0; t < tris.length; t += 9) {
    let hit = true;
    for (let k = 0; k < 3 && hit; k++) {
      const v0 = tris[t + k];
      const v1 = tris[t + 3 + k];
      const v2 = tris[t + 6 + k];
      if (Math.min(v0, v1, v2) > hi[k] || Math.max(v0, v1, v2) < lo[k]) hit = false;
    }
    if (hit) for (let k = 0; k < 9; k++) out.push(tris[t + k]);
  }
  return new Float32Array(out);
}

/** 삼각형 집합의 정점과 중심을 표본점으로 (상한 SAMPLE_N) */
function trisToPts(tris: Float32Array): Float32Array {
  const triCount = Math.floor(tris.length / 9);
  const step = Math.max(1, Math.ceil((triCount * 4) / SAMPLE_N));
  const out: number[] = [];
  for (let t = 0; t < triCount; t += step) {
    const o = t * 9;
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let k = 0; k < 3; k++) {
      out.push(tris[o + k * 3], tris[o + k * 3 + 1], tris[o + k * 3 + 2]);
      cx += tris[o + k * 3];
      cy += tris[o + k * 3 + 1];
      cz += tris[o + k * 3 + 2];
    }
    out.push(cx / 3, cy / 3, cz / 3);
  }
  return new Float32Array(out);
}

/**
 * 점이 삼각형 집합 안쪽에 있는지 — +x 레이가 표면을 홀수 번 지나면 내부다.
 * (Möller-Trumbore 레이-삼각형 교차)
 *
 * **관통을 잡으려면 이게 필요하다.** 표면 거리만 보면 부품이 상대 안에 박혀 있을 때
 * 「멀다」고 나온다 — 북통을 매단 끈 끝이 가로대 **안**에 박혀 있는데 표면까지
 * 22mm(가로대 반지름)라서 안 닿는 것으로 판정됐다.
 */
function insideTris(px: number, py: number, pz: number, tris: Float32Array): boolean {
  let hits = 0;
  for (let t = 0; t < tris.length; t += 9) {
    const ax = tris[t], ay = tris[t + 1], az = tris[t + 2];
    const bx = tris[t + 3], by = tris[t + 4], bz = tris[t + 5];
    const cx = tris[t + 6], cy = tris[t + 7], cz = tris[t + 8];
    // 레이 방향 = +x. e1 = b-a, e2 = c-a, h = dir × e2
    const e1y = by - ay, e1z = bz - az, e1x = bx - ax;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    // dir = (1,0,0) → h = dir × e2 = (0*e2z-0*e2y, 0*e2x-1*e2z, 1*e2y-0*e2x)
    const hx = 0, hy = -e2z, hz = e2y;
    const det = e1x * hx + e1y * hy + e1z * hz;
    if (det > -1e-12 && det < 1e-12) continue;
    const inv = 1 / det;
    const sx = px - ax, sy = py - ay, sz = pz - az;
    const u = inv * (sx * hx + sy * hy + sz * hz);
    if (u < 0 || u > 1) continue;
    // q = s × e1
    const qx = sy * e1z - sz * e1y;
    const qy = sz * e1x - sx * e1z;
    const qz = sx * e1y - sy * e1x;
    const v = inv * qx;   // dir · q = (1,0,0) · q = qx
    if (v < 0 || u + v > 1) continue;
    const tHit = inv * (e2x * qx + e2y * qy + e2z * qz);
    if (tHit > 1e-9) hits++;
  }
  return (hits & 1) === 1;
}

/**
 * 두 부품 **표면** 사이의 최소거리.
 *
 * 핵심은 **AABB 겹침 영역으로 먼저 좁히는 것**이다. 접촉은 두 AABB 가 겹치는(또는
 * limit 안에 든) 영역에서만 일어나므로, 그 밖의 삼각형은 볼 필요가 없다. 이렇게
 * 좁히면 표본을 미리 깎지 않아도 되고, 「큰 상판 중앙에 놓인 좁은 기둥」처럼 접촉면이
 * 작은 경우에도 정확하다 — 표본을 조각 전체에 96개만 퍼뜨렸을 때는 원형 탁자 상판이
 * 기둥에서 600mm 떨어졌다고 나왔다(실제로는 붙어 있다).
 */
function sampleDist(a: PartBox, b: PartBox, limit: number, padHint = 0): number {
  if (!a.tris.length || !b.tris.length) return Infinity;
  // padHint 는 「거리를 재려고」 부를 때 쓴다. limit 만으로 좁히면 접촉 판정에는 맞지만
  // 떨어진 부품 사이 거리는 겹침 영역이 비어 Infinity 가 된다.
  const pad = Math.max(limit, padHint, 0.002);
  const lo: number[] = [0, 0, 0];
  const hi: number[] = [0, 0, 0];
  for (let k = 0; k < 3; k++) {
    lo[k] = Math.max(a.box[k], b.box[k]) - pad;
    hi[k] = Math.min(a.box[k + 3], b.box[k + 3]) + pad;
    if (lo[k] > hi[k]) return Infinity;   // 그 축에서 아예 안 겹친다
  }
  const ta = trisInBox(a.tris, lo, hi);
  const tb = trisInBox(b.tris, lo, hi);
  if (!ta.length || !tb.length) return Infinity;
  const lim2 = limit * limit;
  const pa = trisToPts(ta);
  const d1 = ptsToTris2(pa, tb, lim2);
  if (d1 <= lim2) return Math.sqrt(d1);
  const pb = trisToPts(tb);
  const d2 = ptsToTris2(pb, ta, lim2);
  const best = Math.min(d1, d2);
  if (best <= lim2) return Math.sqrt(best);
  // 표면이 멀어도 **박혀 있을** 수 있다. 겹침 영역 안 표본점이 상대 몸통 내부면 닿은
  // 것이다. 전체 삼각형으로 봐야 한다 — 잘린 ta/tb 로는 레이가 열린 단면을 지난다.
  for (let i = 0; i < pa.length; i += 3) {
    if (insideTris(pa[i], pa[i + 1], pa[i + 2], b.tris)) return 0;
  }
  for (let i = 0; i < pb.length; i += 3) {
    if (insideTris(pb[i], pb[i + 1], pb[i + 2], a.tris)) return 0;
  }
  return Math.sqrt(best);
}

/** 방출 시점에 열려 있던 부품 (병합 전이라 상쇄가 없다) */
export interface OpenPart {
  name: string;
  slot: Slot;
  edges: number;
  /** 대략적인 위치 — 어디를 봐야 하는지 */
  at: [number, number, number];
}

/**
 * 닫혀 있는데 면이 안쪽을 향한 부품.
 * 백페이스 컬링으로 통째로 사라져서 '투과'로 보인다. 구멍보다 찾기 어렵다 —
 * 위상은 멀쩡하고 와이어프레임으로도 안 보이기 때문이다.
 */
export interface FlippedPart {
  name: string;
  slot: Slot;
  volume: number;
  at: [number, number, number];
}

/**
 * 슬롯별 지오메트리 누적기.
 * 변환 스택을 갖고 있어 부품을 조립하듯 쌓을 수 있다.
 */
export class MeshWriter {
  private buckets = new Map<Slot, THREE.BufferGeometry[]>();
  private stack: THREE.Matrix4[] = [new THREE.Matrix4()];
  private partName = 'part';
  readonly parts: PartStat[] = [];
  /** 방출 단위 바운딩 박스 — 부품이 서로 붙어 있는지 검사하는 데 쓴다 */
  readonly boxes: PartBox[] = [];
  /**
   * 방출 시점에 닫혀 있지 않던 부품들.
   *
   * 병합한 뒤에 재면 안 된다 — 서로 다른 부품의 열린 테두리가 우연히 같은 좌표에서
   * 만나면 '두 번 쓰인 모서리'로 계산돼 **상쇄**된다. 실제로는 뚫려 있는데 0 이 나온다.
   * 그래서 부품 하나가 writer 에 들어오는 순간 개별로 검사한다.
   */
  readonly openParts: OpenPart[] = [];
  /** 면이 뒤집혀 있어 **자동으로 되돌린** 부품. 기록만 남는다 — 화면에는 문제가 없다 */
  readonly flippedParts: FlippedPart[] = [];
  /** 두께 없이 그리는 게 정상인 부품은 여기에 등록하고 검사에서 뺀다 */
  private thin = false;
  /**
   * **움직이는 부품** (2026-09-11, Chris: 「박스는 뚜껑을 따로 해야 하잖아 … 일체로 만들어서」).
   *
   * `build()` 가 슬롯별로 전부 병합해 버리기 때문에 병합 뒤에는 「어느 부품이 뚜껑이었는지」가
   * 사라진다 — 그래서 여태 뚜껑을 **별도 조각 + 별도 프롭**으로 만들어야 했고, 상자를 옮기면
   * 뚜껑이 따로 놀았다. 여기 담긴 부품은 병합에서 빠져 제 이름의 그룹으로 남고,
   * `Piece.openable` 이 그 그룹을 어떻게 여는지(젖힘·밀림) 말한다.
   */
  private movableName: string | null = null;
  private movableBuckets = new Map<string, Map<Slot, THREE.BufferGeometry[]>>();

  /** 이 안에서 방출한 것은 본체와 병합하지 않고 `name` 그룹으로 따로 남는다 */
  movable(name: string, fn: () => void) {
    const prev = this.movableName;
    this.movableName = name;
    this.part(name);
    try { fn(); } finally { this.movableName = prev; }
  }

  /** 지금 방출이 들어갈 통 — 움직이는 부품이면 그쪽으로 */
  private bucketFor(slot: Slot): THREE.BufferGeometry[] {
    if (this.movableName) {
      let m = this.movableBuckets.get(this.movableName);
      if (!m) { m = new Map(); this.movableBuckets.set(this.movableName, m); }
      let l = m.get(slot); if (!l) { l = []; m.set(slot, l); }
      return l;
    }
    let l = this.buckets.get(slot); if (!l) { l = []; this.buckets.set(slot, l); }
    return l;
  }

  /** 움직이는 부품들을 슬롯별로 병합해 돌려준다. `build()` 뒤에 부른다 */
  buildMovables(): Map<string, Map<Slot, THREE.BufferGeometry>> {
    const out = new Map<string, Map<Slot, THREE.BufferGeometry>>();
    for (const [name, buckets] of this.movableBuckets) {
      const m = new Map<Slot, THREE.BufferGeometry>();
      for (const [slot, list] of buckets) {
        const merged = list.length === 1 ? list[0] : mergeGeometries(list, false);
        if (!merged) continue;
        if (list.length > 1) for (const g of list) g.dispose();
        merged.computeBoundingBox(); merged.computeBoundingSphere();
        m.set(slot, merged);
      }
      if (m.size) out.set(name, m);
    }
    this.movableBuckets.clear();
    return out;
  }

  /** 현재 변환 아래에 지오메트리를 방출한다. geo 의 소유권은 writer 가 가져간다. */
  add(input: THREE.BufferGeometry, slot: Slot, uvTile = 0.9) {
    // ExtrudeGeometry 는 non-indexed, Lathe/Torus 는 indexed 다. 섞이면 mergeGeometries 가
    // 통째로 실패하고 그 슬롯이 조용히 사라진다 — 방출 시점에 하나로 통일한다.
    const geo = input.index ? input.toNonIndexed() : input;
    if (geo !== input) input.dispose();
    geo.applyMatrix4(this.stack[this.stack.length - 1]);
    // uvTile <= 0 이면 지오메트리가 이미 갖고 있는 UV 를 그대로 쓴다.
    // 그림이 그려진 면(무신도·병풍·부적)은 삼평면 UV 로 덮으면 그림이 잘린다.
    if (uvTile > 0) localUV(geo, uvTile);
    else if (!geo.attributes.uv) localUV(geo, 1);
    // 같은 슬롯 안에 정점 컬러가 있는 것과 없는 것이 섞이면 병합이 실패한다.
    // 흰색은 곱셈 항등이라 색을 굽지 않은 부품에는 아무 영향이 없다.
    if (!geo.attributes.color) {
      const n = geo.attributes.position.count;
      const white = new Float32Array(n * 3).fill(1);
      geo.setAttribute('color', new THREE.BufferAttribute(white, 3));
    }
    this.bucketFor(slot).push(geo);   // 움직이는 부품이면 그쪽 통으로 간다

    // 방출 단위 바운딩 박스를 남긴다. thin 여부와 무관하게 기록한다 —
    // 얇은 판도 물건의 일부이므로 연결성 검사에는 들어가야 한다.
    geo.computeBoundingBox();
    if (geo.boundingBox) {
      const b = geo.boundingBox;
      // 정점 컬러 AO 의 틈새 항이 **자기 상자**를 가림으로 세지 않게 번호를 남긴다.
      // 안 남기면 모든 정점이 자기 상자 안이라 물건 전체가 균일하게 어두워진다
      geo.userData.__box = this.boxes.length;
      this.boxes.push({
        name: this.partName,
        slot,
        box: [b.min.x, b.min.y, b.min.z, b.max.x, b.max.y, b.max.z],
        pts: surfaceSamples(geo),
        tris: surfaceTris(geo),
      });
    }

    // 부품 단위 검사 — 병합 전에 해야 상쇄되지 않는다.
    // (1) 뚫렸는가  (2) 닫혔는데 뒤집혔는가. 둘 다 화면에서는 '투과'로 보인다.
    if (!this.thin) {
      const edges = boundaryEdges(geo);
      const c = new THREE.Vector3();
      geo.boundingBox?.getCenter(c);
      const at: [number, number, number] = [+c.x.toFixed(3), +c.y.toFixed(3), +c.z.toFixed(3)];
      if (edges > 0) {
        // 뚫린 것은 **자동으로 고칠 수 없다** — 없는 면을 만들어야 하기 때문이다.
        // 기록해서 tools/solid-check.mjs 가 실패시키게 한다.
        this.openParts.push({ name: this.partName, slot, edges, at });
      } else {
        const vol = signedVolume(geo);
        if (vol < 0) {
          // 뒤집힌 것은 **자동으로 고친다.** 삼각형 감김만 되돌리면 되고 형태는 그대로다.
          // 조각 코드가 프로파일을 어느 방향으로 적었든 결과가 같아야 한다 —
          // 그게 「투과를 시스템으로 막는다」의 실제 구현이다.
          //
          // lathe 안에서도 보정하지만(ensureOutward) 그것만으로는 부족했다:
          // lathe 결과에 rotateZ 를 걸면 좌표가 미세하게 옮겨져 닫힘 판정이 달라지고,
          // 회전 전에는 뚫려 있다고 보고 건너뛴 것이 회전 후에 닫힌 것으로 잡힌다.
          // 그래서 **최종 방출 지점인 여기서** 한 번 더 본다.
          flipWinding(geo);
          this.flippedParts.push({ name: this.partName, slot, volume: +vol.toFixed(6), at });
        }
      }
    }

    const tris = (geo.index ? geo.index.count : geo.attributes.position.count) / 3;
    const last = this.parts[this.parts.length - 1];
    if (last && last.name === this.partName && last.slot === slot) last.triangles += tris;
    else this.parts.push({ name: this.partName, slot, triangles: tris });
  }

  /** 이후 방출되는 지오메트리에 붙일 부품 이름 (진단용) */
  part(name: string) {
    this.partName = name;
    return this;
  }

  /**
   * 두께 없이 그리는 게 정상인 부품 (그림면·종이·가죽막·천).
   * 콜백 안에서 방출된 것은 닫힘 검사에서 제외된다 — '실수로 열린 것'과
   * '의도적으로 얇은 것'을 코드에서 구분하기 위한 장치다.
   */
  thinSurface(fn: () => void) {
    const prev = this.thin;
    this.thin = true;
    fn();
    this.thin = prev;
  }

  /** 변환을 밀어넣고 콜백 안에서만 적용한다 */
  at(x: number, y: number, z: number, fn: () => void, rotY = 0, rotX = 0, rotZ = 0) {
    const m = new THREE.Matrix4()
      .makeTranslation(x, y, z)
      .multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rotX, rotY, rotZ)));
    this.stack.push(new THREE.Matrix4().multiplyMatrices(this.stack[this.stack.length - 1], m));
    fn();
    this.stack.pop();
  }

  /** 좌우 대칭 부품을 한 번에 (다리, 손잡이 등) */
  mirrorX(x: number, y: number, z: number, fn: (side: number) => void) {
    for (const s of [-1, 1]) this.at(x * s, y, z, () => fn(s));
  }

  /**
   * 정점 컬러에 **가림(AO)을 구워 넣는다.**
   *
   * 왜 이렇게 하나 — 조명을 늘리면 GPU 가 나가고, 텍스처를 늘리면 메모리가 나간다.
   * 정점 컬러는 이미 모든 슬롯에 흰색(곱셈 항등)으로 들어가 있어서 **런타임 비용이 0** 이다.
   * 절차 생성물이 「상자를 쌓아 놓은 것」처럼 보이는 이유는 면마다 밝기가 같기 때문이고,
   * 그 하나만 깨도 형태가 산다.
   *
   * 세 항을 곱한다:
   *  1. **하늘 가림** — 아래를 보는 면은 하늘이 안 보이니 어둡다. 반구광을 구운 것과 같다
   *  2. **접지** — 바닥에 가까운 띠를 눌러 물건이 바닥에 「앉게」 만든다. 뜬 느낌이 사라진다
   *  3. **틈새** — 다른 부품의 상자에 가까운 정점을 눌러 이음매·서랍틈·다리 안쪽을 어둡게 한다
   *
   * 정점 밀도에 의존하므로 큰 평면 하나(정점 4개)에서는 3번이 거의 안 먹는다.
   * 그건 의도한 것이다 — 그런 면은 어차피 텍스처가 먹인다.
   */
  private bakeAO() {
    if (!this.boxes.length) return;
    let y0 = Infinity;
    let y1 = -Infinity;
    for (const b of this.boxes) {
      if (b.box[1] < y0) y0 = b.box[1];
      if (b.box[4] > y1) y1 = b.box[4];
    }
    const span = Math.max(y1 - y0, 1e-3);
    // 접지 띠는 물건 높이에 비례한다. 화이트보드와 USB 에 같은 3cm 를 쓰면 하나는 안 보이고
    // 하나는 통째로 검어진다
    const foot = span * 0.22;

    // 틈새 항에 쓸 부품 상자. 큰 것부터 열여섯 개만 본다 — 정점수 × 상자수가 로딩 시간이다
    const solids = this.boxes
      .map((b, i) => ({
        i,
        b: b.box,
        v: (b.box[3] - b.box[0]) * (b.box[4] - b.box[1]) * (b.box[5] - b.box[2]),
      }))
      .filter((o) => o.v > 1e-7)
      .sort((a, z) => z.v - a.v)
      .slice(0, 16);
    // 틈새 반경도 물건 크기에 비례한다
    const R = Math.max(span * 0.05, 0.004);

    for (const [, list] of this.buckets) {
      for (const geo of list) {
        const pos = geo.attributes.position;
        const nor = geo.attributes.normal;
        const col = geo.attributes.color as THREE.BufferAttribute | undefined;
        if (!col) continue;
        const own = (geo.userData.__box as number) ?? -1;
        const heavy = pos.count > 24000; // 무거운 부품은 틈새 항을 건너뛴다
        for (let k = 0; k < pos.count; k++) {
          const x = pos.getX(k);
          const y = pos.getY(k);
          const z = pos.getZ(k);

          // 1. 하늘 가림
          const ny = nor ? nor.getY(k) : 0;
          let a = 0.66 + 0.34 * (ny * 0.5 + 0.5);

          // 2. 접지
          const t = Math.min(1, Math.max(0, (y - y0) / foot));
          a *= 0.74 + 0.26 * t;

          // 3. 틈새
          if (!heavy) {
            let near = 0;
            for (const s of solids) {
              if (s.i === own) continue;
              const dx = Math.max(s.b[0] - x, 0, x - s.b[3]);
              if (dx > R) continue;
              const dy = Math.max(s.b[1] - y, 0, y - s.b[4]);
              if (dy > R) continue;
              const dz = Math.max(s.b[2] - z, 0, z - s.b[5]);
              if (dz > R) continue;
              const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
              const w = 1 - d / R;
              if (w > near) near = w;
            }
            a *= 1 - 0.3 * near * near;
          }

          col.setXYZ(k, col.getX(k) * a, col.getY(k) * a, col.getZ(k) * a);
        }
        col.needsUpdate = true;
      }
    }
  }

  build(): Map<Slot, THREE.BufferGeometry> {
    this.bakeAO();
    const out = new Map<Slot, THREE.BufferGeometry>();
    for (const [slot, list] of this.buckets) {
      const merged = list.length === 1 ? list[0] : mergeGeometries(list, false);
      if (!merged) continue;
      if (list.length > 1) for (const g of list) g.dispose();
      merged.computeBoundingBox();
      merged.computeBoundingSphere();
      out.set(slot, merged);
    }
    this.buckets.clear();
    return out;
  }
}

/**
 * 로컬 위치 기반 삼평면 UV.
 * ExtrudeGeometry/LatheGeometry 의 UV 는 파라미터 공간이라 면마다 밀도가 제각각이다.
 * 실제 크기로 다시 씌워야 나뭇결이 부품 크기와 무관하게 일정해진다.
 */
export function localUV(geo: THREE.BufferGeometry, tile: number) {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  if (!pos || !nor) return geo;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const ax = Math.abs(nor.getX(i));
    const ay = Math.abs(nor.getY(i));
    const az = Math.abs(nor.getZ(i));
    let u: number;
    let v: number;
    if (ax >= ay && ax >= az) {
      u = pos.getZ(i);
      v = pos.getY(i);
    } else if (ay >= ax && ay >= az) {
      u = pos.getX(i);
      v = pos.getZ(i);
    } else {
      u = pos.getX(i);
      v = pos.getY(i);
    }
    uv[i * 2] = u / tile;
    uv[i * 2 + 1] = v / tile;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geo;
}

// --- 기본 입체 ---

/**
 * 모서리를 깎은 상자. 가구의 기본 단위.
 * 완전한 직각 모서리는 근거리에서 종이처럼 보인다 — 베벨이 만드는 가는 하이라이트가
 * "깎아 만든 물건"이라는 인상을 준다.
 */
export function bevelBox(w: number, h: number, d: number, bevel = 0.004, segs = 1): THREE.BufferGeometry {
  const b = Math.min(bevel, w / 2.5, h / 2.5, d / 2.5);
  const shape = roundedRect(w - b * 2, h - b * 2, Math.min(b * 1.5, (w - b * 2) / 2, (h - b * 2) / 2));
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(1e-4, d - b * 2),
    bevelEnabled: true,
    bevelSize: b,
    bevelThickness: b,
    bevelSegments: segs,
    curveSegments: segs + 1,
    steps: 1,
  });
  geo.translate(0, 0, -(d - b * 2) / 2);
  geo.computeVertexNormals();
  return geo;
}

/** 모서리가 둥근 사각형 Shape (중심 원점) */
export function roundedRect(w: number, h: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = w / 2;
  const y = h / 2;
  const rr = Math.max(0, Math.min(r, x, y));
  s.moveTo(-x + rr, -y);
  s.lineTo(x - rr, -y);
  s.quadraticCurveTo(x, -y, x, -y + rr);
  s.lineTo(x, y - rr);
  s.quadraticCurveTo(x, y, x - rr, y);
  s.lineTo(-x + rr, y);
  s.quadraticCurveTo(-x, y, -x, y - rr);
  s.lineTo(-x, -y + rr);
  s.quadraticCurveTo(-x, -y, -x + rr, -y);
  return s;
}

/**
 * 회전체. 다리·손잡이·다이얼처럼 선반(旋盤)으로 깎은 부품에 쓴다.
 * profile 은 [반지름, 높이] 쌍이며 위로 올라가는 순서.
 *
 * **양 끝을 자동으로 막는다.** LatheGeometry 는 옆면만 만들기 때문에 그대로 두면
 * 위아래가 뚫린 통이 되고, 뒷면 컬링 때문에 앞면이 투과돼 속이 들여다보인다.
 * (초·손잡이·향로처럼 축에 닿지 않는 프로파일에서 반드시 생긴다.)
 */
export function lathe(profile: [number, number][], segments = 20, cap = true): THREE.BufferGeometry {
  const pts = profile.map(([r, y]) => new THREE.Vector2(Math.max(1e-4, r), y));
  const geo = new THREE.LatheGeometry(pts, segments);
  geo.computeVertexNormals();
  if (!cap) return geo;

  // 축에 닿는 끝은 r 을 1e-4 로 강제하므로, 그보다 작은 임계로 판정해야 캡이 붙는다
  const EPS = 1e-5;
  const parts: THREE.BufferGeometry[] = [geo];
  const first = pts[0];
  const last = pts[pts.length - 1];
  // 프로파일이 이미 닫혀 있으면(첫=끝) 막을 필요가 없다
  const closed = first.distanceTo(last) < EPS;

  /**
   * 캡은 LatheGeometry 와 **같은 파라미터화**로 직접 만든다.
   * CircleGeometry 는 (cos, sin) 순서로 정점을 놓는데 LatheGeometry 는 (sin, cos) 라서,
   * 그대로 붙이면 테두리 정점이 어긋나 구멍이 오히려 늘어난다.
   */
  const disc = (r: number, y: number, up: boolean) => {
    const pos: number[] = [];
    const push = (x: number, yy: number, z: number) => pos.push(x, yy, z);
    for (let i = 0; i < segments; i++) {
      const p0 = (i / segments) * Math.PI * 2;
      const p1 = ((i + 1) / segments) * Math.PI * 2;
      const ax = r * Math.sin(p0);
      const az = r * Math.cos(p0);
      const bx = r * Math.sin(p1);
      const bz = r * Math.cos(p1);
      push(0, y, 0);
      if (up) {
        push(ax, y, az);
        push(bx, y, bz);
      } else {
        push(bx, y, bz);
        push(ax, y, az);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.computeVertexNormals();
    return g;
  };

  if (!closed) {
    // 아래 끝: 바깥을 향하는 방향이 -Y
    if (first.x > EPS) parts.push(disc(first.x, first.y, first.y > last.y));
    if (last.x > EPS) parts.push(disc(last.x, last.y, last.y >= first.y));
  }
  // 캡이 하나도 안 붙는 경우(폐단면 프로파일)도 면 방향 보정은 거쳐야 한다.
  // 여기서 그냥 return 하면 두께 있는 통·깔때기가 보정을 건너뛴다 — 실제로 그랬다.
  if (parts.length === 1) return ensureOutward(geo);
  // lathe 는 uv 를 갖고 캡은 없다 — 속성 구성이 다르면 병합이 통째로 실패한다
  for (const p of parts) {
    if (!p.attributes.uv) {
      const n = p.attributes.position.count;
      p.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    }
    if (p.index) {
      const nonIdx = p.toNonIndexed();
      const i = parts.indexOf(p);
      p.dispose();
      parts[i] = nonIdx;
    }
  }

  const merged = mergeGeometries(parts, false);
  if (!merged) return geo;
  for (const p of parts) p.dispose();
  merged.computeVertexNormals();
  return ensureOutward(merged);
}

/**
 * 닫힌 몸체의 면이 밖을 보게 보정한다.
 *
 * **회전체는 프로파일을 어느 방향으로 적었는지에 따라 면이 통째로 뒤집힌다.**
 * 폐단면(두께 있는 통·깔때기)에서 특히 그렇다 — 바깥면을 아래에서 위로 적으면 맞고
 * 위에서 아래로 적으면 반대가 되는데, 코드를 보고 그걸 알아채기는 어렵다.
 * 뒤집힌 면은 화면에서 앞면이 사라져 속이 보이는 것으로 나타난다.
 *
 * 그래서 만들고 나서 부피를 재고 음수면 되돌린다. 프로파일을 어떻게 적어도 결과가
 * 같아지므로, 이 함수가 있으면 그 실수를 **애초에 할 수 없다.**
 * (뚫려 있으면 부피가 의미 없으므로 건드리지 않는다 — 그건 solid-check 가 잡는다.)
 */
export function ensureOutward(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  if (boundaryEdges(geo) > 0) return geo;
  if (signedVolume(geo) < 0) flipWinding(geo);
  return geo;
}

/**
 * 끝을 막은 부분 토러스 — 굽은 관(수전 목, 고리, 손잡이)에 쓴다.
 * `TorusGeometry` 를 arc 로 잘라 쓰면 양 끝 단면이 뚫린다.
 */
export function cappedArc(
  radius: number,
  tube: number,
  arc: number,
  tubularSegments = 18,
  radialSegments = 8,
): THREE.BufferGeometry {
  const body = new THREE.TorusGeometry(radius, tube, radialSegments, tubularSegments, arc);
  const parts: THREE.BufferGeometry[] = [body.index ? body.toNonIndexed() : body];
  if (body.index) body.dispose();

  // TorusGeometry 와 같은 파라미터화로 끝 단면을 덮는다
  const at = (u: number, v: number): [number, number, number] => [
    (radius + tube * Math.cos(v)) * Math.cos(u),
    (radius + tube * Math.cos(v)) * Math.sin(u),
    tube * Math.sin(v),
  ];
  for (const [u, flip] of [
    [0, true],
    [arc, false],
  ] as [number, boolean][]) {
    const pos: number[] = [];
    const cx = radius * Math.cos(u);
    const cy = radius * Math.sin(u);
    for (let j = 0; j < radialSegments; j++) {
      const a = at(u, (j / radialSegments) * Math.PI * 2);
      const b = at(u, ((j + 1) / radialSegments) * Math.PI * 2);
      pos.push(cx, cy, 0);
      if (flip) pos.push(...b, ...a);
      else pos.push(...a, ...b);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
    g.computeVertexNormals();
    parts.push(g);
  }

  const merged = mergeGeometries(parts, false);
  if (!merged) return parts[0];
  for (const p of parts) p.dispose();
  merged.computeVertexNormals();
  return merged;
}

/**
 * 사각 테두리 몰딩.
 *
 * 프로파일(안쪽→바깥쪽 오프셋 u, 높이 v)을 안쪽 사각형을 따라 스윕한다.
 * 링을 통째로 오프셋하므로 네 모서리의 마이터가 저절로 맞는다 — 변 4개를 따로
 * 만들어 45도로 자르는 것보다 이음매가 깨끗하다.
 */
export function frameMolding(
  innerW: number,
  innerD: number,
  profile: { u: number; v: number }[],
): THREE.BufferGeometry {
  const rings: number[][] = [];
  for (const p of profile) {
    const x = innerW / 2 + p.u;
    const z = innerD / 2 + p.u;
    // 링 한 바퀴 (닫힘). 시작점을 두 번 넣어 UV/노멀 심을 의도적으로 만든다.
    rings.push([-x, p.v, -z, x, p.v, -z, x, p.v, z, -x, p.v, z, -x, p.v, -z]);
  }

  const pos: number[] = [];
  const idx: number[] = [];
  const perRing = 5;
  for (const r of rings) pos.push(...r);

  for (let i = 0; i < rings.length - 1; i++) {
    for (let j = 0; j < perRing - 1; j++) {
      const a = i * perRing + j;
      const b = a + 1;
      const c = a + perRing;
      const d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }

  // 캡(중심까지 채우는 팬)을 붙이면 안 된다 — 몰딩은 테두리 '띠'라서 안쪽이 통째로
  // 메워진다. 닫으려면 프로파일 단면 자체를 닫아야 한다 (moldingProfile 의 close).
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  return geo;
}

/**
 * 원형 몰딩 — 같은 프로파일을 사각이 아니라 원 경로로 스윕한다.
 * 원형 천판에 사각 몰딩을 두르면 네 모서리가 천판 밖으로 삐져나온다.
 */
export function roundMolding(
  innerR: number,
  profile: { u: number; v: number }[],
  segments = 40,
): THREE.BufferGeometry {
  return lathe(
    profile.map((p) => [innerR + p.u, p.v] as [number, number]),
    segments,
    false,
  );
}

/** 몰딩 프로파일 생성기 — 명명된 로브의 합 (crown / bead / cove) */
export interface Lobe {
  kind: 'crown' | 'bead' | 'cove' | 'fillet';
  /** 프로파일 위 위치 0..1 */
  at: number;
  /** 폭 */
  spread: number;
  /** 튀어나온(+) / 파인(-) 정도 */
  depth: number;
}

export function moldingProfile(
  width: number,
  height: number,
  lobes: Lobe[],
  samples = 20,
  close = true,
): { u: number; v: number }[] {
  const out: { u: number; v: number }[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    let u = t * width;
    for (const l of lobes) {
      const g = Math.exp(-(((t - l.at) / l.spread) ** 2));
      if (l.kind === 'crown') u += l.depth * width * Math.sin(Math.PI * t) ** 0.6;
      else u += l.depth * width * g;
    }
    out.push({ u, v: (1 - t) * height });
  }

  // 단면을 닫는다: 바깥 끝에서 뒤로 → 안쪽으로 → 시작점으로.
  // 닫아야 스윕 결과가 '띠'가 아니라 속이 찬 사각 고리가 되고, 뒤에서 봐도 뚫리지 않는다.
  if (close) {
    const first = out[0];
    const last = out[out.length - 1];
    const back = -height * 0.14;
    out.push({ u: last.u, v: back });
    out.push({ u: first.u, v: back });
    out.push({ u: first.u, v: first.v });
    // 닫힌 단면의 회전 방향이 면 방향을 정한다. 이 방향으로 돌면 면이 안쪽을 향해
    // 백페이스 컬링으로 통째로 사라진다(=투과). 뒤집어서 바깥을 향하게 한다.
    out.reverse();
  }
  return out;
}

// --- 반복 구조 ---

/** 널판을 이어 붙인 판재 (상자, 문짝). 판 사이 홈이 근거리에서 결정적이다. */
export function plankPanel(
  w: number,
  h: number,
  thickness: number,
  planks: number,
  gap = 0.004,
): THREE.BufferGeometry[] {
  const pw = (w - gap * (planks - 1)) / planks;
  const out: THREE.BufferGeometry[] = [];
  for (let i = 0; i < planks; i++) {
    const g = bevelBox(pw, h, thickness, 0.003);
    g.translate(-w / 2 + pw / 2 + i * (pw + gap), 0, 0);
    out.push(g);
  }
  return out;
}

// --- 진단 ---

/**
 * 부품 덩이 나누기 — 서로 닿아 있는 부품끼리 묶는다.
 *
 * 반환값의 길이가 1 보다 크면 **부품이 서로 떨어져 있다.** 그건 대개 좌표를 잘못 준
 * 것이고 화면에서는 「파츠가 공중에 떠 있다」로 보인다. 다만 한 조각에 여러 물건을 담는
 * 경우(도자기 한 벌, 쟁반 세 벌)는 정상이므로 Piece.clusters 로 몇 덩이까지 정상인지
 * 선언할 수 있다.
 *
 * @param tol 이 거리 안이면 닿은 것으로 본다. bevelBox 기본 베벨이 4mm 라 그보다 넉넉해야
 *   맞대어 놓은 부품이 떨어진 것으로 잡히지 않는다.
 */
/** shellBox 에서 비울 면 */
export type ShellFace = '+x' | '-x' | '+y' | '-y' | '+z' | '-z';

/**
 * 판으로 짜는 **상자 껍데기**. 여섯 면을 다 만들고 `open` 에 적은 면만 비운다.
 *
 * 왜 헬퍼가 필요한가: 여행 가방 뚜껑을 판 넉 장(겉면·앞턱·좌·우)으로 짜면서 **힌지 쪽
 * 뒷턱을 빼먹었다.** 열면 그쪽이 뚫려 안이 들여다보였는데 어느 게이트도 잡지 못했다 —
 * 판마다는 닫힌 몸체라 위상적으로 구멍이 없고, 「있어야 할 판이 없다」는 건 의도를
 * 알아야 판단되기 때문이다. 기하학으로 못 잡는 실수는 **API 로 막는 게 맞다.**
 * 이 함수를 쓰면 비울 면을 반드시 적어야 하므로 빼먹을 수가 없다.
 *
 * 반환은 지오메트리 배열이다 — 회전해서 붙여야 하는 경우(열린 뚜껑)를 위해 호출자가
 * 변환을 걸 수 있게 남긴다.
 */
export function shellBox(
  sx: number,
  sy: number,
  sz: number,
  t: number,
  open: ShellFace[] = [],
  bevel = 0.003,
): THREE.BufferGeometry[] {
  const skip = new Set(open);
  const out: THREE.BufferGeometry[] = [];
  const put = (face: ShellFace, w: number, h: number, d: number, x: number, y: number, z: number) => {
    if (skip.has(face)) return;
    const g = bevelBox(w, h, d, Math.min(bevel, Math.min(w, h, d) * 0.3));
    g.translate(x, y, z);
    out.push(g);
  };
  // 아래·위 — 가로 전체를 덮는다
  put('-y', sx, t, sz, 0, t / 2, 0);
  put('+y', sx, t, sz, 0, sy - t / 2, 0);
  // 앞·뒤 — 위아래 판 사이를 메운다
  const midH = sy - t * 2;
  put('-z', sx, midH, t, 0, t + midH / 2, -sz / 2 + t / 2);
  put('+z', sx, midH, t, 0, t + midH / 2, sz / 2 - t / 2);
  // 좌·우 — 앞뒤 판 사이를 메운다
  put('-x', t, midH, sz - t * 2, -sx / 2 + t / 2, t + midH / 2, 0);
  put('+x', t, midH, sz - t * 2, sx / 2 - t / 2, t + midH / 2, 0);
  return out;
}

export { sampleDist };

export function partClusters(boxes: PartBox[], tol = 0.006): PartBox[][] {
  const n = boxes.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const near = (a: PartBox, b: PartBox) => {
    // 1차 — AABB 로 빠르게 배제한다. 한 축이라도 tol 보다 벌어져 있으면 실제로도
    // 벌어져 있다 (AABB 는 실제 부피를 감싸므로 이 방향의 판정은 옳다).
    for (let k = 0; k < 3; k++) {
      const gap = Math.max(a.box[k] - b.box[k + 3], b.box[k] - a.box[k + 3]);
      if (gap > tol) return false;
    }
    // 2차 — **AABB 가 겹쳐도 닿았다는 뜻은 아니다.** 기울어지거나 회전한 부품의 AABB 는
    // 실제 부피보다 훨씬 커서, 걸레 자루(양동이 밖을 지나감)와 여행 가방 뚜껑(힌지에서
    // 14mm 떨어짐)이 둘 다 「닿음」으로 통과했다. 표면 표본으로 실제 거리를 잰다.
    return sampleDist(a, b, tol) <= tol;
  };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (!near(boxes[i], boxes[j])) continue;
      const ra = find(i);
      const rb = find(j);
      if (ra !== rb) parent[ra] = rb;
    }
  }
  const groups = new Map<number, PartBox[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(boxes[i]);
    else groups.set(r, [boxes[i]]);
  }
  // 큰 덩이부터 — 첫 번째가 「몸통」이고 나머지가 떨어져 나간 것이다
  return [...groups.values()].sort((a, b) => b.length - a.length);
}

export interface ModelStats {
  triangles: number;
  slots: { slot: Slot; triangles: number }[];
  parts: PartStat[];
  size: [number, number, number];
  /** 부호 있는 부피 — 음수면 면이 뒤집혔다는 뜻 (와이어프레임으로는 못 본다) */
  signedVolume: number;
  /**
   * 경계 모서리 수 — 한 번만 쓰인 모서리. 0 이 아니면 그 부분에 '구멍'이 있다.
   * 뚜껑 없는 회전체, 두께 없는 띠가 여기 걸린다. 앞면이 투과돼 뒷면이 보이는 현상의 원인.
   */
  openEdges: number;
  openBySlot: { slot: Slot; edges: number }[];
  /** 부품 단위로 잡은 구멍 — 이게 진짜 원인 목록이다 */
  openParts: OpenPart[];
  /** 방출 단위 바운딩 박스 — partClusters 로 부품 연결성을 본다 */
  boxes: PartBox[];
  /** 부품 단위로 잡은 뒤집힌 면 */
  flippedParts: FlippedPart[];
}

export function analyze(
  slots: Map<Slot, THREE.BufferGeometry>,
  parts: PartStat[],
  openParts: OpenPart[] = [],
  flippedParts: FlippedPart[] = [],
  boxes: PartBox[] = [],
): ModelStats {
  const box = new THREE.Box3();
  let tris = 0;
  let vol = 0;
  let open = 0;
  const bySlot: { slot: Slot; triangles: number }[] = [];
  const openBySlot: { slot: Slot; edges: number }[] = [];

  for (const [slot, geo] of slots) {
    const t = (geo.index ? geo.index.count : geo.attributes.position.count) / 3;
    tris += t;
    bySlot.push({ slot, triangles: t });
    geo.computeBoundingBox();
    if (geo.boundingBox) box.union(geo.boundingBox);
    vol += signedVolume(geo);
    const e = boundaryEdges(geo);
    open += e;
    if (e > 0) openBySlot.push({ slot, edges: e });
  }

  const size = new THREE.Vector3();
  box.getSize(size);
  return {
    triangles: tris,
    slots: bySlot.sort((a, b) => b.triangles - a.triangles),
    parts,
    size: [+size.x.toFixed(3), +size.y.toFixed(3), +size.z.toFixed(3)],
    signedVolume: +vol.toFixed(4),
    openEdges: open,
    openBySlot: openBySlot.sort((a, b) => b.edges - a.edges),
    openParts: openParts.slice().sort((a, b) => b.edges - a.edges),
    flippedParts: flippedParts.slice().sort((a, b) => a.volume - b.volume),
    boxes,
  };
}

/**
 * 경계 모서리 수.
 * 닫힌 메시에서는 모든 모서리가 정확히 두 삼각형에 공유된다. 한 번만 쓰인 모서리가
 * 있으면 거기가 뚫려 있다는 뜻이고, 뒷면 컬링 때문에 '앞면이 투과돼 보이는' 현상이 된다.
 * 정점 인덱스가 아니라 **좌표**로 묶는다 (같은 자리의 다른 정점도 같은 모서리로 본다).
 */
export function boundaryEdges(geo: THREE.BufferGeometry): number {
  const pos = geo.attributes.position;
  const index = geo.index;
  const n = index ? index.count : pos.count;
  /* 좌표는 정점마다 **한 번만** 번호로 바꾸고, 모서리는 번호 쌍의 숫자 키로 센다.
   * 예전엔 삼각형마다 모서리 문자열 셋을 이어 붙여 Map 에 넣었다 — 조각 하나 지을 때마다 부품마다
   * 돌아서(MeshWriter.add) 로딩의 큰 몫이었다(2026-09-25 측정). 판정은 같다: 같은 좌표 = 같은 정점 */
  // 정점 번호: 0.1mm 격자 정수 (x,y) 를 숫자 하나로 묶고 z 는 안쪽 Map 으로 — 문자열 없이 같은 판정.
  // ±52m 밖(조각 로컬에선 없다)이면 문자열 키로 떨어진다
  const q = 1e4, OFF = 2 ** 19, SPAN = 2 ** 20;
  const byXY = new Map<number, Map<number, number>>();
  const far = new Map<string, number>();
  let nextId = 0;
  const vid = new Int32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const x = Math.round(pos.getX(i) * q), y = Math.round(pos.getY(i) * q), z = Math.round(pos.getZ(i) * q);
    let id: number | undefined;
    if (Math.abs(x) < OFF && Math.abs(y) < OFF) {
      const xy = (x + OFF) * SPAN + (y + OFF);
      let zs = byXY.get(xy);
      if (!zs) { zs = new Map(); byXY.set(xy, zs); }
      id = zs.get(z);
      if (id === undefined) { id = nextId++; zs.set(z, id); }
    } else {
      const k = `${x},${y},${z}`;
      id = far.get(k);
      if (id === undefined) { id = nextId++; far.set(k, id); }
    }
    vid[i] = id;
  }
  const M = nextId;   // 모서리 키 a·M + b — 정점이 백만 개여도 2^53 안이다
  const counts = new Map<number, number>();
  const bump = (a: number, b: number) => {
    const k = a < b ? a * M + b : b * M + a;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  };
  for (let i = 0; i < n; i += 3) {
    const a = vid[index ? index.getX(i) : i];
    const b = vid[index ? index.getX(i + 1) : i + 1];
    const c = vid[index ? index.getX(i + 2) : i + 2];
    // 퇴화 삼각형은 세지 않는다
    if (a === b || b === c || a === c) continue;
    bump(a, b);
    bump(b, c);
    bump(c, a);
  }
  let open = 0;
  for (const c of counts.values()) if (c === 1) open++;
  return open;
}

/** 닫힌 메시의 부호 있는 부피. 와인딩이 뒤집히면 음수가 된다. */
/**
 * 삼각형 감김을 뒤집는다 — 면 방향이 반대로 만들어진 지오메트리를 되돌린다.
 * 정점 순서를 바꾸고 노멀을 다시 계산하므로 겉보기 형태는 그대로다.
 */
export function flipWinding(geo: THREE.BufferGeometry) {
  if (geo.index) {
    const idx = geo.index.array as Uint16Array | Uint32Array;
    for (let i = 0; i < idx.length; i += 3) {
      const t = idx[i];
      idx[i] = idx[i + 2];
      idx[i + 2] = t;
    }
    geo.index.needsUpdate = true;
  } else {
    const pos = geo.attributes.position;
    const a = pos.array as Float32Array;
    const it = geo.attributes.uv?.array as Float32Array | undefined;
    for (let i = 0; i < pos.count; i += 3) {
      for (let k = 0; k < 3; k++) {
        const t = a[i * 3 + k];
        a[i * 3 + k] = a[(i + 2) * 3 + k];
        a[(i + 2) * 3 + k] = t;
      }
      if (it) {
        for (let k = 0; k < 2; k++) {
          const t = it[i * 2 + k];
          it[i * 2 + k] = it[(i + 2) * 2 + k];
          it[(i + 2) * 2 + k] = t;
        }
      }
    }
    pos.needsUpdate = true;
    if (geo.attributes.uv) geo.attributes.uv.needsUpdate = true;
  }
  geo.computeVertexNormals();
  return geo;
}

export function signedVolume(geo: THREE.BufferGeometry): number {
  const pos = geo.attributes.position;
  const index = geo.index;
  const n = index ? index.count : pos.count;
  let v = 0;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (let i = 0; i < n; i += 3) {
    const ia = index ? index.getX(i) : i;
    const ib = index ? index.getX(i + 1) : i + 1;
    const ic = index ? index.getX(i + 2) : i + 2;
    a.fromBufferAttribute(pos, ia);
    b.fromBufferAttribute(pos, ib);
    c.fromBufferAttribute(pos, ic);
    v += a.dot(b.clone().cross(c)) / 6;
  }
  return v;
}
